export interface DisplayOption {
    value: string;
    label: string;
    size: string;
    multiLayoutOptions: MultiLayoutOptions[];
}
export interface LayoutItem {
    id: string;
    name: string;
}

export type FilterableLayoutItem = LayoutItem & {
    sizes?: string[];
    colors?: string[];
    description?: string;
};

export interface FilterOption {
    value: string;
    label: string;
}

export interface FullLayoutItem extends LayoutItem {
    xmlContent: any;
    purpose: string;
    description: string;
    images: string[];
    size: string;
    number: string;
    colors: string[];
    bpp: number | null;
    resolution?: string;
}

export interface GetLayoutsRequest {
    search_by: 'store' | 'corporate';
    search_id: number;
    data_per_page: number;
    current_page: number;
    f_layout_type: '' | 'single_layout' | 'multiple_layout';
    f_layout_size: string;
    f_layout_name: string;
    f_layout_width: number | null;
    f_layout_height: number | null;
}

export interface LayoutItemFromAPI {
    id: number;
    store_id?: number;
    layout_name: string;
    layout_no: string;
    layout_size: string;
    layout_width: number;
    layout_height: number;
    layout_image: string | null;
    layout_purpose: number;
    layout_capacity: number;
    layout_bpp: number | null;
    layout_type: string;
}

export interface GetLayoutsResponse {
    data: any;
    aaData: LayoutItemFromAPI[];
}

// ─────────────────────────────────────────────────────────────
// “Create layout” API request/response payloads
// ─────────────────────────────────────────────────────────────

export interface CreateLayoutRequest {
    store_id: number;
    number?: string | null;
    name: string;
    purpose: number;
    capacity: number;
    sub_layout_pos_size?: string;
    fit_from_single?: boolean;
    profile_fit_single?: string;
    content: string;
}

export interface CreateLayoutResponse {
    data: any;
    id: number;
    store_id: number;
    layout_no: number;
    layout_name: string;
    layout_size: string;
    layout_width: number;
    layout_height: number;
    fields: string;
    layout_purpose: number;
    layout_capacity: number;
    layout_bpp: number;
}

// ─────────────────────────────────────────────────────────────
// “Layout packages” API response payloads
// ─────────────────────────────────────────────────────────────

export interface LayoutVersion {
    id: number;
    version_number: number;
    description: string;
    hash: string;
    is_active: number;
    created_at: string;
    updated_at: string;
}

export interface LayoutPackage {
    description: string;
    id: number;
    group_id: number;
    name: string;
    slug: string;
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    latest_version: LayoutVersion;
    images: string[];
    versions: LayoutVersion[];
}

export interface GetLayoutPackagesResponse {
    data: any;
    corporate_id: number;
    layout_packages: LayoutPackage[];
}

export interface LayoutCategory {
    id: number;
    category_name: string;
    category_identifier: string;
    image_resolution: string;
    multi_layouts: MultiLayoutOptions[];
}

export interface DetailLayoutEntry {
    color_profile: string;
    layout_size: any;
    id: number;
    layout_capacity: number;
    layout_name: string;
    layout_purpose: number;
    sub_layout_pos_size: string | null;
    layout_fields: string;
    layout_img: string;
    bpp: number;
}
export interface MultiLayoutDetailEntry {
    id: number;
    sublayout_fields: string;
    sublayout_name: string;
    sublayout_purpose: number;
    sublayout_img: string;
}

export type DisplayTypeValue = number[] | { default_layout: number };

export interface DisplayTypeRelations {
    [displayType: string]: DisplayTypeValue;
}

export interface MultiDisplayTypeRelations {
    [displayType: string]: {
        [layout: string]: DisplayTypeValue;
    };
}
export type LayoutPackageDetails = {
    layouts: DetailLayoutEntry[];
    display_type_relations: DisplayTypeRelations;
    multi_display_type_relations: MultiDisplayTypeRelations;
    sub_layouts: MultiLayoutDetailEntry[];
};
export interface LayoutPackageDetailData {
    id: number;
    group_id: number;
    name: string;
    slug: string;
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    barcode_for_layout_preview_detail: string;
    details: {
        parsing_status: boolean;
        reason: string;
        packages: LayoutPackageDetails;
    };
}

export interface ProductListItem {
    id: number;
    store_id: number;
    store_name: string;
    product_status: number;
    product_id: number;
    barcode: string;
    number: string;
    name: string;
    area: string;
    unit: string;
    price: string;
    promo_price: string | null;
    custom_fields: string;
    custom_fields_array: {
        index: number;
        value: string;
    }[];
    default_layout: number;
    shelf: string[];
    img_path: string | null;
    created_at: string | null;
    updated_at: string;
    has_devices: boolean;
}

export interface ProductListRequest {
    search: string;
}

export interface ProductListResponse {
    aaData: ProductListItem[];
}

export interface GeneratePreviewRequest {
    layout_package_id: number;
    products: string[];
}
export interface GeneratePreviewResponse {
    generate_preview_status: boolean;
    message: string;
    result: Record<string, [boolean, string, string]>;
}

export interface CreateLayoutPackageRequest {
    package_name: string;
    package_description: string;
    create_empty_layout_package: boolean;
    packages: {
        layouts: Array<{
            id: number;
            layout_capacity: number;
            layout_name: string;
            layout_purpose: number;
            sub_layout_pos_size: string;
            layout_fields: string;
        }>;
        display_type_relations: DisplayTypeRelations;
        sub_layouts: Array<{
            id: number;
            layout_capacity: number;
            sublayout_name: string;
            sublayout_purpose: number;
            sub_layout_pos_size: string;
            sublayout_fields: string;
        }>;
        multi_display_type_relations: MultiDisplayTypeRelations;
    };
}
export interface CreateLayoutPackageResponse {
    data: { id: number };
}

export type UpdateLayoutPackageRequest = CreateLayoutPackageRequest;
export interface UpdateLayoutPackageResponse {
    data: { id: number };
}

// ─────────────────────────────────────────────────────────────
// ** New: “Check” & “Apply” layout-package endpoints **
// ─────────────────────────────────────────────────────────────

/** Payload for POST /layout-package/:cid/check */
export interface CheckLayoutStoresRequest {
    layout_package_id: number;
    version_number?: number | null;
    assigned_stores: number[];
}

/** Response data for POST /layout-package/:cid/check */
export interface CheckLayoutStoresResponse {
    eligible_stores: number[];
    stores_with_existing_lp: number[];
    ineligible_stores: Record<number, number>;
    layout_token: string;
    token_timeout_minutes: number;
}

/** One store’s result in the “apply” response */
export interface ApplyStoreResult {
    store_id: number;
    apply_status: boolean;
    /** [successFlag, code, message] */
    raw_result: [boolean, string, string];
}

/** Response data for POST /layout-package/:cid/apply */
export interface ApplyLayoutPackageResponse {
    status: number;
    message: string;
    data: ApplyStoreResult[];
}

export interface ImportLayoutPackageResponse {
    status: number;
    message: string;
    data: {
        layout_token?: string;
        information?: {
            layout_package: Record<string, any>;
            versions: Array<Record<string, any>>;
        };
    };
}

/** Multiple Layout */
export type LayoutDialogResult = {
    name: string;
    content: string;
    purpose: number;
};
export enum MULTI_LAYOUT_SLUG {
    COLUMN_2 = 'column-2',
    COLUMN_3 = 'column-3',
    LANDSCAPE_GRID_4 = 'landscape-grid-4',
    LANDSCAPE_LIST_2 = 'landscape-list-2',
    LANDSCAPE_LIST_5 = 'landscape-list-5',
    LANDSCAPE_RANGE_6 = 'landscape-range-6',
    LIST_2 = 'list-2',
    PORTRAIT_GRID_4 = 'portrait-grid-4',
    PORTRAIT_LIST_3 = 'portrait-list-3',
    PORTRAIT_LIST_6 = 'portrait-list-6',
    PORTRAIT_RANGE_8 = 'portrait-range-8',
}
export interface MultiLayoutOptions {
    capacity: number;
    id: number;
    name: string;
    slug: MULTI_LAYOUT_SLUG;
    sub_layout_columns: number;
    sub_layout_rows: number;
}
export enum LAYOUT_MODE {
    SINGLE = 'SINGLE',
    MULTI = 'MULTI',
}
export interface GenericLayoutProps {
    layoutInfo: LayoutPackageDetailData | null;
    isLoading: boolean;
    isNew: boolean;
}
export type DisplayTypeKey = string;
export type StyleSlug = string;
export type LayoutId = number;
export type defaultLayout = {
    default_layout: number;
};
export type MultiRelationsMap = Record<DisplayTypeKey, Record<StyleSlug, LayoutId[] | defaultLayout>>;
export type MultiFormateRelationMap = Record<
    DisplayTypeKey,
    Record<StyleSlug, { list: LayoutList[]; default_layout: string | null }>
>;
export type SingleRelationsMap = Record<string, LayoutId[] | defaultLayout>;
export type SingleFormateRelationMap = Record<DisplayTypeKey, { list: LayoutList[]; default_layout: string | null }>;

export type Dialogs = {
    edit: boolean;
    new: boolean;
    deleteAll: boolean;
};
export type MultiState = {
    options: DisplayOption[];
    displayType: DisplayTypeKey;
    styleId: number | null;
    styleSlug: StyleSlug | null;
    layoutsById: Record<LayoutId, FullLayoutItem>;
    relations: MultiFormateRelationMap;
    isDirty: boolean;
    searchSub: string;
    bust: number;
    selectedLayouts: string[];
    selectedColors: string[];
    editLayout: FullLayoutItem | null;
    dialogs: Dialogs;
};
export interface NewSubLayout {
    [key: number]: FullLayoutItem;
}
export type Action =
    | {
          type: 'INIT';
          payload: { options: DisplayOption[]; subLayouts: FullLayoutItem[]; relations: MultiRelationsMap };
      }
    | { type: 'SET_RELATIONS'; payload: { displayType: string; selectedId: LayoutId[]; styleSlug: string } }
    | { type: 'ADD_NEW_SUB_LAYOUT'; payload: NewSubLayout }
    | { type: 'DELETE_SUB_LAYOUT'; payload: string }
    | { type: 'DELETE_SUB_LAYOUT_PACKAGE' }
    | { type: 'SET_DISPLAY_TYPE'; displayType: string }
    | { type: 'SET_STYLE_ID'; styleId: number | null }
    | { type: 'SET_SEARCH_SUB'; payload: string }
    | { type: 'REORDER'; ids: LayoutId[] }
    | { type: 'ATTACH'; ids: LayoutId[] }
    | { type: 'DETACH'; ids: LayoutId[] }
    | { type: 'CLEAN_SELECTED_LAYOUT' }
    | {
          type: 'TOGGLE_SELECTED_LAYOUT';
          payload: {
              id: string;
              checked: boolean;
          };
      }
    | {
          type: 'SET_SELECTED_COLORS';
          payload: string[];
      }
    | { type: 'CLEAR_EDIT_DATA' }
    | {
          type: 'UPDATE_EDIT_DATA';
          payload: FullLayoutItem;
      }
    | {
          type: 'STORE_EDIT_DATA';
          payload: {
              id: string;
              colors: string[];
              updated: {
                  name: string;
                  purpose: number;
                  content: string;
              };
          };
      }
    | {
          type: 'TOGGLE_DIALOG';
          payload: {
              key: string;
          };
      };

export const COLOR_MAP: Record<string, { hex: string; label: string }> = {
    b: { hex: '#000000', label: 'Black' },
    w: { hex: '#FFFFFF', label: 'White' },
    r: { hex: '#FF0000', label: 'Red' },
    y: { hex: '#FFFF00', label: 'Yellow' },
};

export const RESOLUTION_TO_SIZE: Record<string, string> = {
    '152x152': '1.60',
    '200x200': '1.60',
    '212x104': '2.20',
    '250x122': '2.20',
    '296x128': '2.90',
    '296x152': '2.70',
    '400x300': '4.20',
    '600x488': '5.65',
    '880x528': '7.50',
    '960x640': '11.60',
    '1024x768': '8.40',
    '800x480': '7.50',
    '200x384': '2.70',
};

export const BPP_TO_CODES: Record<string, string[]> = {
    '1': ['b', 'w'], // Black + White
    '2': ['b', 'w', 'r'], // + Red
    '3': ['b', 'w', 'r', 'y'], // + Yellow
};

export type LayoutList = {
    id: string;
    name: string;
    uuid: string;
};

export const COLOR_OPTION = [
    { value: '1', label: '2-colors (BW)' },
    { value: '2', label: '3-colors (BWR)' },
    { value: '3', label: '4-colors (BWRY)' },
];
